import { supabase } from "@/integrations/supabase/client";

export interface ShiftCodeSegment {
  label?: string;
  start_time: string;
  end_time: string;
}

export interface ShiftCode {
  id: string;
  company_id: string;
  code: string;
  name: string;
  start_time: string | null;
  end_time: string | null;
  default_hours: number | null;
  color: string;
  category: string;
  is_active: boolean;
  is_system: boolean;
  is_split_shift: boolean;
  split_segments: ShiftCodeSegment[] | null;
  created_at: string;
  updated_at: string;
}

export interface ShiftCodeInput {
  code: string;
  name: string;
  start_time?: string | null;
  end_time?: string | null;
  default_hours?: number | null;
  color?: string;
  category?: 'work' | 'off' | 'break' | 'training' | 'work_split_shift';
  is_active?: boolean;
  is_split_shift?: boolean;
  split_segments?: ShiftCodeSegment[] | null;
}

/**
 * Fetch all shift codes for the current company
 */
export async function fetchAllShiftCodes(): Promise<ShiftCode[]> {
  // Ensure user context is set before making queries that involve RLS policies
  await supabase.rpc('ensure_user_context');
  
  const { data, error } = await supabase
    .from('shift_codes')
    .select('*')
    .eq('is_active', true)
    .order('category', { ascending: true })
    .order('code', { ascending: true });

  if (error) {
    console.error('Error fetching shift codes:', error);
    throw new Error(error.message || 'Failed to fetch shift codes');
  }

  // Cast and ensure split shift fields have defaults
  return (data || []).map((code: any) => ({
    ...code,
    is_system: code.is_system || false,
    is_split_shift: code.is_split_shift || false,
    split_segments: code.split_segments || null,
  })) as ShiftCode[];
}

/**
 * Create a new shift code
 */
export async function createShiftCode(input: ShiftCodeInput): Promise<ShiftCode> {
  // Get current authenticated user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get user's profile with company_id
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('user_id', user.id)
    .single();

  if (profileError || !profile?.company_id) {
    console.error('Error fetching profile:', profileError);
    throw new Error('Company ID not found');
  }

  const { data, error } = await supabase
    .from('shift_codes')
    .insert({
      company_id: profile.company_id,
      code: input.code.toUpperCase(),
      name: input.name,
      start_time: input.start_time && input.start_time.trim() !== '' ? input.start_time : null,
      end_time: input.end_time && input.end_time.trim() !== '' ? input.end_time : null,
      default_hours: input.default_hours || null,
      color: input.color || '#3b82f6',
      category: input.category || 'work',
      is_active: input.is_active !== false,
      is_split_shift: input.is_split_shift || false,
      split_segments: input.split_segments || null,
    } as any)
    .select()
    .single();

  if (error) {
    console.error('Error creating shift code:', error);
    throw new Error(error.message || 'Failed to create shift code');
  }

  const resultData: any = data;
  return {
    ...resultData,
    is_system: resultData.is_system || false,
    is_split_shift: resultData.is_split_shift || false,
    split_segments: resultData.split_segments || null,
  } as ShiftCode;
}

/**
 * Update an existing shift code
 */
export async function updateShiftCode(id: string, input: Partial<ShiftCodeInput>): Promise<ShiftCode> {
  // Check if this is a system shift code — block code/category/active changes
  const { data: existing, error: fetchError } = await supabase
    .from('shift_codes')
    .select('is_system, code')
    .eq('id', id)
    .single();

  if (fetchError) {
    throw new Error('Failed to verify shift code');
  }

  if (existing?.is_system) {
    // System codes: only allow name, color changes
    if (input.code !== undefined && input.code.toUpperCase() !== existing.code) {
      throw new Error('Cannot change the code of a system shift code');
    }
    if (input.category !== undefined) {
      throw new Error('Cannot change the category of a system shift code');
    }
    if (input.is_active === false) {
      throw new Error('Cannot deactivate a system shift code');
    }
  }

  const updateData: any = {
    updated_at: new Date().toISOString(),
  };

  if (input.code !== undefined) updateData.code = input.code.toUpperCase();
  if (input.name !== undefined) updateData.name = input.name;
  if (input.start_time !== undefined) {
    updateData.start_time = input.start_time && input.start_time.trim() !== '' ? input.start_time : null;
  }
  if (input.end_time !== undefined) {
    updateData.end_time = input.end_time && input.end_time.trim() !== '' ? input.end_time : null;
  }
  if (input.default_hours !== undefined) updateData.default_hours = input.default_hours;
  if (input.color !== undefined) updateData.color = input.color;
  if (input.category !== undefined) updateData.category = input.category;
  if (input.is_active !== undefined) updateData.is_active = input.is_active;
  if (input.is_split_shift !== undefined) updateData.is_split_shift = input.is_split_shift;
  if (input.split_segments !== undefined) updateData.split_segments = input.split_segments;

  const { data, error } = await supabase
    .from('shift_codes')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating shift code:', error);
    throw new Error(error.message || 'Failed to update shift code');
  }

  const resultData: any = data;
  return {
    ...resultData,
    is_system: resultData.is_system || false,
    is_split_shift: resultData.is_split_shift || false,
    split_segments: resultData.split_segments || null,
  } as ShiftCode;
}

/**
 * Delete (soft delete) a shift code by setting is_active to false
 */
export async function deleteShiftCode(id: string): Promise<void> {
  // Check if this is a system shift code
  const { data: existing } = await supabase
    .from('shift_codes')
    .select('is_system')
    .eq('id', id)
    .single();

  if (existing?.is_system) {
    throw new Error('Cannot deactivate a system shift code');
  }

  const { error } = await supabase
    .from('shift_codes')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    console.error('Error deleting shift code:', error);
    throw new Error(error.message || 'Failed to delete shift code');
  }
}

/**
 * Permanently delete a shift code from the database
 */
export async function hardDeleteShiftCode(id: string): Promise<void> {
  // Check if this is a system shift code
  const { data: existing } = await supabase
    .from('shift_codes')
    .select('is_system')
    .eq('id', id)
    .single();

  if (existing?.is_system) {
    throw new Error('Cannot permanently delete a system shift code');
  }

  const { error } = await supabase
    .from('shift_codes')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error permanently deleting shift code:', error);
    throw new Error(error.message || 'Failed to permanently delete shift code');
  }
}
